import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useWatch } from 'react-hook-form';
import { useParams } from 'react-router';
import { Button, Column, ConfirmPopup, Icon, Row, Text } from '~/commonComponents';
import { getUserEducation } from '~/actions';
import { clearWorkingInterviews } from '~/actions/PermanentJobs';
import { UserProfession } from '~/enums/UserProfession';
import { useAppSelector, useAutoSave, useToggle } from '~/hooks';
import { InfoSection } from './components/InfoSection';
import { GeneralJobInfoForm } from './components/GeneralJobInfoForm';
import { ScheduleForm } from './components/ScheduleForm';
import { JobDescriptionForm } from './components/JobDescriptionForm';
import { IJobDetailsFormProps } from './Types';
import { Colors } from '~/themes/colors';
import { AutoSaveStatus } from './components/AutoSaveStatus';
import { useJobDetailsForm } from './hooks/useJobDetailsForm';
import { useSaveDraftJobPost } from '~/domain/PermanentJobs';
import { JobDetailsFormMapper } from './mappers/JobDetailsFormMapper';

/**
 * JobDetailsForm - Reusable form wrapper for job details
 *
 * This component wraps all job detail form sections and manages form state.
 * It can be used for both creating new jobs and editing existing jobs.
 *
 * @example
 * <JobDetailsForm
 *   submitButtonText="Next"
 *   onSubmit={handleNext}
 * />
 */
export const JobDetailsForm = ({
  submitButtonText = 'Next',
  onSubmit: onSubmitCallback,
  initialValues,
  editMode = false,
  scrollToSection,
  dataCy,
  dataTestId,
}: IJobDetailsFormProps) => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const [showErrorBanner, setShowErrorBanner] = useState(false);
  const [showProfessionConfirmation, showProfessionConfirmationControl] = useToggle(false);
  const [showSavedMessage, showSavedMessageControl] = useToggle(false);
  const [pendingFieldChange, setPendingFieldChange] = useState<{
    newValue: any;
    onChange: (value: any) => void;
  } | null>(null);

  // Refs for scrolling to sections
  const generalSectionRef = useRef<HTMLDivElement>(null);
  const scheduleSectionRef = useRef<HTMLDivElement>(null);
  const descriptionSectionRef = useRef<HTMLDivElement>(null);

  // Get working interviews count from Redux
  const workingInterviewsCount = useAppSelector((state) => {
    if (!id) return 0;
    return state.permanentJobs.workingInterviews[id]?.length ?? 0;
  });

  // Initialize react-hook-form with Zod validation
  const {
    handleSubmit,
    control,
    setValue,
    trigger,
    formState: { errors, isSubmitted },
    watch,
  } = useJobDetailsForm({ initialValues });

  const { mutateAsync: saveDraft, isPending } = useSaveDraftJobPost();

  const { lastSavedAt } = useAutoSave({
    watch,
    saveFn: () => {
      const payload = JobDetailsFormMapper.toSaveDraftPayload(watch());
      return saveDraft({ id: id!, payload });
    },
    initialValues,
  });

  // Refs for auto-save status animation control
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const showSavedMessageRef = useRef(showSavedMessage);

  // Keep ref in sync with state
  useEffect(() => {
    showSavedMessageRef.current = showSavedMessage;
  }, [showSavedMessage]);

  // Handle auto-save status display with dismiss-and-reshow on new saves
  useEffect(() => {
    if (!lastSavedAt) return;

    // Clear any pending hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }

    const isCurrentlyVisible = showSavedMessageRef.current;

    if (isCurrentlyVisible) {
      // Hide first to trigger exit animation
      showSavedMessageControl.setOff();
    }

    // Delay showing only if we need to wait for exit animation
    const showDelay = isCurrentlyVisible ? 450 : 0;

    const showTimeoutId = setTimeout(() => {
      showSavedMessageControl.setOn();

      // Schedule hide after 3 seconds
      hideTimeoutRef.current = setTimeout(() => {
        showSavedMessageControl.setOff();
      }, 3000);
    }, showDelay);

    return () => {
      clearTimeout(showTimeoutId);
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastSavedAt]);

  // Watch specific fields for reactive logic in parent component
  const professionType = useWatch({ control, name: 'professionType' });
  const officeSpecialty = useWatch({ control, name: 'officeSpecialty' });
  const compensationType = useWatch({ control, name: 'compensationType' });
  const minRate = useWatch({ control, name: 'minRate' });
  const maxRate = useWatch({ control, name: 'maxRate' });

  // Dispatch getUserEducation when professionType changes
  useEffect(() => {
    if (professionType) {
      const professionCode = UserProfession.getNormalizedCode(professionType);
      dispatch(getUserEducation({ profession: professionCode }));
    }
  }, [professionType, dispatch]);

  // Trigger validation when specialty changes after form submission
  useEffect(() => {
    if (isSubmitted && officeSpecialty) {
      trigger(['officeSpecialty']);
    }
  }, [professionType, officeSpecialty, isSubmitted, trigger]);

  // Trigger validation when minRate/maxRate change after form submission
  // This is needed because the validation is in form-level refine(), not field-level
  useEffect(() => {
    if (isSubmitted && compensationType) {
      trigger(['minRate', 'maxRate']);
    }
  }, [minRate, maxRate, isSubmitted, compensationType, trigger]);

  // Scroll to section when in edit mode
  useEffect(() => {
    if (editMode && scrollToSection) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        let targetRef: React.RefObject<HTMLDivElement | null> | null = null;

        switch (scrollToSection) {
          case 'general':
            targetRef = generalSectionRef;
            break;
          case 'schedule':
            targetRef = scheduleSectionRef;
            break;
          case 'description':
            targetRef = descriptionSectionRef;
            break;
        }

        if (targetRef?.current) {
          // Get the element's position
          const elementPosition = targetRef.current.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - 100; // 100px offset from top

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
          });
        }
      }, 100);
    }
  }, [editMode, scrollToSection]);

  // Handle field change with confirmation logic
  const handleFieldChange = (newValue: any, fieldOnChange: (value: any) => void) => {
    if (workingInterviewsCount > 0) {
      // Store pending change and show confirmation
      setPendingFieldChange({ newValue, onChange: fieldOnChange });
      showProfessionConfirmationControl.setOn();
    } else {
      // Apply change directly
      fieldOnChange(newValue);
    }
  };

  // Handle profession type confirmation
  const handleProfessionConfirmation = (confirmed: boolean) => {
    if (confirmed && pendingFieldChange) {
      // Apply the pending change
      pendingFieldChange.onChange(pendingFieldChange.newValue);
      // Clear working interviews
      if (id) {
        dispatch(clearWorkingInterviews(id));
      }
    }
    // Close popup and clear pending change
    showProfessionConfirmationControl.setOff();
    setPendingFieldChange(null);
  };

  // Form submit handler
  const onSubmit = handleSubmit(
    (data) => {
      // Form is valid
      setShowErrorBanner(false);
      if (onSubmitCallback) {
        onSubmitCallback(data);
      }
    },
    () => {
      setShowErrorBanner(true);
      // Form has errors - scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
  );

  return (
    <>
      {/* Auto Save Status */}
      <AutoSaveStatus visible={showSavedMessage} date={lastSavedAt} />

      <Column width={'60%'} alignSelf="center" gap={20} data-cy={dataCy} data-testid={dataTestId}>
        {/* Error Banner */}
        {showErrorBanner && (
          <Row gap={6} alignItems="center">
            <Icon name="alert-circle" />
            <Text color={Colors.error_500}>
              Oops! It looks like a few required fields were left empty.
            </Text>
          </Row>
        )}

        {/* General Job Information Section */}
        <div ref={generalSectionRef}>
          <InfoSection icon="briefcase-solid" title="General Job Information">
            <GeneralJobInfoForm
              control={control}
              errors={errors}
              setValue={setValue}
              onFieldChange={handleFieldChange}
            />
          </InfoSection>
        </div>

        {/* Schedule Section */}
        <div ref={scheduleSectionRef}>
          <InfoSection icon="calendar-solid" title="Schedule">
            <ScheduleForm control={control} errors={errors} />
          </InfoSection>
        </div>

        {/* Job Description Section */}
        <div ref={descriptionSectionRef}>
          <InfoSection icon="document-solid" title="Job Description" required>
            <JobDescriptionForm control={control} errors={errors} />
          </InfoSection>
        </div>

        {/* Submit Button */}
        {onSubmitCallback && (
          <Row justifyContent="center" marginTop={24}>
            <Button text={submitButtonText} onClick={onSubmit} isLoading={isPending} />
          </Row>
        )}
      </Column>

      {/* Profession Type Change Confirmation Modal */}
      {showProfessionConfirmation && (
        // @ts-expect-error - ConfirmPopup is not typed
        <ConfirmPopup
          Icon={() => <Icon name="warning-outline" size={80} color="warning_500" />}
          leftButtonAction={() => handleProfessionConfirmation(false)}
          leftButtonText="Go Back"
          rightButtonAction={() => handleProfessionConfirmation(true)}
          rightButtonText="Continue"
          title="Before You Continue"
          titleStyle={{ marginBottom: 20, marginTop: 20 }}
          descriptionStyle={{ marginBottom: 40 }}
          description="You’re about to make changes that will affect your working interviews. If you move forward, you’ll need to recreate them to match the updated job details. Do you want to continue?"
        />
      )}
    </>
  );
};
